/* Poke-Geek V3 — Product accordion root-cause fix + global description specifications placement.
   The original inline accordion script forces every other <details> element to close
   when a new panel opens. Closing a long product description while the visitor is
   scrolled inside it drastically shortens the document and makes the browser clamp
   the viewport to the bottom of the page.

   Product specifications remain inside the description accordion on every product.
   Structured Poke-Geek descriptions keep their full-width banner and explicit intro.
   Classic descriptions use their first meaningful introductory content so the
   specifications can sit consistently in the upper-right column on desktop.
*/

(() => {
  if (window.__pgV3ProductAccordionNativeFixReady) return;
  window.__pgV3ProductAccordionNativeFixReady = true;

  const selector = '[data-pg-product-accordion]';

  const normalize = (value) => (value || '').replace(/\s+/g, ' ').trim();

  const getMeaningfulElementChildren = (element) =>
    Array.from(element?.children || []).filter((child) => {
      if (!(child instanceof Element)) return false;
      if (child.matches('.pg-pdp-specs, .pg-pdp-description-intro-grid')) return false;
      if (normalize(child.textContent).length > 0) return true;
      return Boolean(child.querySelector('img, video, iframe, table'));
    });

  const findDescriptionFlowRoot = (descriptionBlock) => {
    let current = descriptionBlock;

    for (let depth = 0; depth < 6; depth += 1) {
      if (!(current instanceof Element)) return null;

      const children = getMeaningfulElementChildren(current);
      if (children.length >= 2) return current;
      if (children.length !== 1) return null;

      current = children[0];
    }

    return null;
  };

  const removeV2FlowClasses = (element) => {
    if (!(element instanceof Element)) return;

    element.classList.remove(
      'pg-pdp-description-lead',
      'pg-pdp-description-detail',
      'pg-pdp-description-lead-support'
    );
  };

  const looksLikeVisualBanner = (element) => {
    if (!(element instanceof Element)) return false;

    const className = normalize(element.getAttribute('class')).toLowerCase();
    const style = normalize(element.getAttribute('style')).toLowerCase();
    const blockName = normalize(element.getAttribute('data-pg-block')).toLowerCase();

    return (
      blockName === 'banner' ||
      className.includes('banner') ||
      className.includes('hero') ||
      style.includes('linear-gradient') ||
      style.includes('radial-gradient')
    );
  };

  const isIntroCandidate = (element) => {
    if (!(element instanceof Element)) return false;
    if (element.matches('.pg-pdp-specs, .pg-pdp-description-intro-grid')) return false;

    const className = normalize(element.getAttribute('class')).toLowerCase();
    if (
      className.includes('cards') ||
      className.includes('card-grid') ||
      className.includes('features') ||
      className.includes('specs') ||
      className.includes('characteristics') ||
      className.includes('shipping') ||
      className.includes('delivery')
    ) {
      return false;
    }

    if (
      element.matches('table, ul, ol, figure, img, video, iframe') ||
      element.querySelector('table, figure, img, video, iframe')
    ) {
      return false;
    }

    const text = normalize(element.textContent);
    if (!text) return false;
    if (text.length > 1800 && element.children.length > 2) return false;

    return true;
  };

  const prepareSpecs = (specs) => {
    specs.classList.remove('pg-pdp-specs--summary', 'pg-pdp-specs--in-flow');
    specs.classList.add('pg-pdp-specs--description-aside');
  };

  const markPlaced = (root, mode) => {
    root.dataset.pgSpecsDescriptionPosition = mode;
    return true;
  };

  const placeStructuredSpecs = (root, target, specs, descriptionBlock) => {
    const templateRoot =
      target.querySelector('[data-pg-template]') ||
      descriptionBlock?.querySelector?.('[data-pg-template]') ||
      (descriptionBlock?.matches?.('[data-pg-template]') ? descriptionBlock : null);

    if (!templateRoot) return false;

    const intro = templateRoot.querySelector('[data-pg-block="intro"]');
    if (!intro) return false;

    const banner = templateRoot.querySelector('[data-pg-block="banner"]');
    const cards = templateRoot.querySelector('[data-pg-block="cards"]');
    const legacyLeadColumn = templateRoot.querySelector(':scope > .pg-pdp-description-lead-column');

    templateRoot.classList.remove('pg-pdp-description-flow');
    removeV2FlowClasses(banner);
    removeV2FlowClasses(intro);
    removeV2FlowClasses(cards);
    prepareSpecs(specs);

    let grid = templateRoot.querySelector(':scope > .pg-pdp-description-intro-grid');

    if (!grid) {
      grid = document.createElement('div');
      grid.className = 'pg-pdp-description-intro-grid';
    }

    if (banner) {
      if (banner.parentElement !== templateRoot || templateRoot.firstElementChild !== banner) {
        templateRoot.insertBefore(banner, templateRoot.firstElementChild);
      }

      if (banner.nextElementSibling !== grid) {
        banner.insertAdjacentElement('afterend', grid);
      }
    } else if (grid.parentElement !== templateRoot) {
      templateRoot.insertBefore(grid, templateRoot.firstElementChild);
    }

    if (intro.parentElement !== grid) grid.append(intro);
    if (specs.parentElement !== grid) grid.append(specs);

    if (cards && grid.nextElementSibling !== cards) {
      grid.insertAdjacentElement('afterend', cards);
    }

    if (legacyLeadColumn?.isConnected) {
      const leftovers = Array.from(legacyLeadColumn.children);
      let anchor = cards || grid;

      leftovers.forEach((child) => {
        anchor.insertAdjacentElement('afterend', child);
        anchor = child;
      });

      legacyLeadColumn.remove();
    }

    return markPlaced(root, 'top-right-structured');
  };

  const unwrapLegacyLeadColumn = (flowRoot, specs) => {
    const legacyLeadColumn = flowRoot?.querySelector?.(':scope > .pg-pdp-description-lead-column');
    if (!(legacyLeadColumn instanceof HTMLElement)) return;

    if (specs.parentElement === legacyLeadColumn) {
      legacyLeadColumn.insertAdjacentElement('afterend', specs);
    }

    Array.from(legacyLeadColumn.children).forEach((child) => {
      legacyLeadColumn.insertAdjacentElement('beforebegin', child);
    });

    legacyLeadColumn.remove();
  };

  const placeGenericSpecs = (root, target, specs, descriptionBlock) => {
    if (!(descriptionBlock instanceof Element)) return false;

    let flowRoot = findDescriptionFlowRoot(descriptionBlock);

    if (!flowRoot) {
      flowRoot = descriptionBlock.parentElement === target ? target : descriptionBlock;
    }

    if (!(flowRoot instanceof Element)) return false;

    flowRoot.classList.remove('pg-pdp-description-flow');
    unwrapLegacyLeadColumn(flowRoot, specs);

    let items = getMeaningfulElementChildren(flowRoot);
    items.forEach(removeV2FlowClasses);

    const banner = items.length > 1 && looksLikeVisualBanner(items[0]) ? items[0] : null;
    const candidates = items.filter((item) => item !== banner && item !== specs);
    const selected = [];
    let textBudget = 0;

    for (const item of candidates) {
      if (!isIntroCandidate(item)) {
        if (selected.length > 0) break;
        continue;
      }

      const textLength = normalize(item.textContent).length;
      if (selected.length > 0 && textBudget + textLength > 1200) break;

      selected.push(item);
      textBudget += textLength;

      if (selected.length >= 2) break;
    }

    if (!selected.length) return false;

    prepareSpecs(specs);

    let grid = flowRoot.querySelector(':scope > .pg-pdp-description-intro-grid');
    if (!grid) {
      grid = document.createElement('div');
      grid.className = 'pg-pdp-description-intro-grid pg-pdp-description-intro-grid--generic';
    }

    let introCopy = grid.querySelector(':scope > .pg-pdp-description-intro-copy');
    if (!introCopy) {
      introCopy = document.createElement('div');
      introCopy.className = 'pg-pdp-description-intro-copy';
    }

    if (banner) {
      if (banner.nextElementSibling !== grid) {
        banner.insertAdjacentElement('afterend', grid);
      }
    } else {
      const reference = selected[0];
      if (grid.parentElement !== flowRoot || grid.nextElementSibling !== reference) {
        flowRoot.insertBefore(grid, reference);
      }
    }

    if (introCopy.parentElement !== grid) grid.append(introCopy);
    selected.forEach((item) => introCopy.append(item));
    if (specs.parentElement !== grid) grid.append(specs);

    return markPlaced(root, 'top-right-global');
  };

  const placeSpecsInDescriptionIntro = (root) => {
    if (!(root instanceof HTMLElement)) return false;

    const section = root.closest('.product-information');
    const target = root.querySelector('.pg-pdp-long-content__description-target');
    const specs =
      root.querySelector('.pg-pdp-specs') ||
      section?.querySelector('.product-details .pg-pdp-specs--summary') ||
      section?.querySelector('.pg-pdp-specs--summary') ||
      section?.querySelector('.pg-pdp-specs');

    if (!target || !specs) return false;

    const existingGrid = specs.closest('.pg-pdp-description-intro-grid');
    if (existingGrid && target.contains(existingGrid)) {
      prepareSpecs(specs);
      return markPlaced(root, existingGrid.classList.contains('pg-pdp-description-intro-grid--generic') ? 'top-right-global' : 'top-right-structured');
    }

    const descriptionBlock =
      target.querySelector('.pg-pdp-description-block') ||
      target.firstElementChild;

    if (!descriptionBlock) return false;

    if (placeStructuredSpecs(root, target, specs, descriptionBlock)) return true;
    return placeGenericSpecs(root, target, specs, descriptionBlock);
  };

  const placeSpecsWithRetry = (root, attempts = 100) => {
    if (placeSpecsInDescriptionIntro(root) || attempts <= 0) return;

    window.setTimeout(() => {
      placeSpecsWithRetry(root, attempts - 1);
    }, 50);
  };

  const watchDescriptionRelocation = (root) => {
    if (!(root instanceof HTMLElement)) return;
    if (root.dataset.pgSpecsPlacementObserved === 'true') return;

    root.dataset.pgSpecsPlacementObserved = 'true';
    const section = root.closest('.product-information') || root;

    const observer = new MutationObserver(() => {
      placeSpecsInDescriptionIntro(root);
    });

    observer.observe(section, {
      childList: true,
      subtree: true,
    });

    window.setTimeout(() => observer.disconnect(), 12000);
  };

  const detachExclusiveAccordionHandlers = (root) => {
    if (!(root instanceof HTMLElement)) return null;

    placeSpecsInDescriptionIntro(root);

    if (root.dataset.pgNativeAccordionReady === 'true') {
      placeSpecsWithRetry(root);
      watchDescriptionRelocation(root);
      return root;
    }

    // cloneNode preserves the complete rendered content and current open states,
    // but intentionally does not copy JavaScript event listeners attached to the nodes.
    const cleanRoot = root.cloneNode(true);
    cleanRoot.dataset.pgNativeAccordionReady = 'true';
    root.replaceWith(cleanRoot);

    placeSpecsWithRetry(cleanRoot);
    watchDescriptionRelocation(cleanRoot);
    return cleanRoot;
  };

  const initialize = (scope = document) => {
    if (scope instanceof Element && scope.matches(selector)) {
      detachExclusiveAccordionHandlers(scope);
      return;
    }

    scope.querySelectorAll?.(selector).forEach(detachExclusiveAccordionHandlers);
  };

  // Deferred theme scripts execute after the inline listener has been attached,
  // so replacing the accordion here removes the problematic listener at the source.
  initialize();

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => initialize(), { once: true });
  } else {
    initialize();
  }

  window.addEventListener('load', () => initialize(), { once: true });

  // Keep the same behaviour when Shopify reloads the product section in the editor.
  document.addEventListener('shopify:section:load', (event) => {
    initialize(event.target);
  });
})();

/* Poke-Geek V3 — Premium policy content polish. */
(() => {
  if (window.__pgV3PolicyAccordionPolishReady) return;
  window.__pgV3PolicyAccordionPolishReady = true;

  const emojiPattern = /[\u{1F1E6}-\u{1F1FF}\u{1F300}-\u{1FAFF}\u{2600}-\u{27BF}]\uFE0F?/gu;
  const normalize = (value) => (value || '').replace(/\s+/g, ' ').trim();

  const injectStyles = () => {
    if (document.getElementById('PgV3PolicyAccordionPolishStyles')) return;

    const style = document.createElement('style');
    style.id = 'PgV3PolicyAccordionPolishStyles';
    style.textContent = `
      .template-product.pg-theme-v2 .pg-pdp-accordion__panel--policy {
        padding-bottom: 1.22rem;
        line-height: 1.62;
      }

      .template-product.pg-theme-v2 .pg-pdp-accordion__panel--policy p {
        margin-bottom: 0.56rem;
      }

      .template-product.pg-theme-v2 .pg-pdp-policy-heading {
        display: flex;
        align-items: flex-start;
        gap: 0.55rem;
        max-width: 88ch;
        margin: 0.92rem 0 0.34rem !important;
        color: #10244c;
        font-weight: 780;
        line-height: 1.42;
      }

      .template-product.pg-theme-v2 .pg-pdp-accordion__panel--policy > .pg-pdp-policy-heading:first-child {
        margin-top: 0 !important;
      }

      .template-product.pg-theme-v2 .pg-pdp-policy-heading::before {
        content: '';
        width: 0.42rem;
        height: 0.42rem;
        flex: 0 0 0.42rem;
        margin-top: 0.5em;
        border-radius: 0.13rem;
        background: #2b62c9;
        box-shadow: 0 0 0 3px rgb(43 98 201 / 8%);
      }

      .template-product.pg-theme-v2 .pg-pdp-policy-list {
        gap: 0.32rem;
        margin: 0.42rem 0 0.68rem;
      }

      .template-product.pg-theme-v2 .pg-pdp-policy-list li {
        padding-left: 1.02rem;
      }

      .template-product.pg-theme-v2 .pg-pdp-policy-list li::before {
        width: 0.28rem;
        height: 0.28rem;
      }

      .template-product.pg-theme-v2 .pg-pdp-policy-note {
        max-width: 88ch !important;
        margin: 0.62rem 0 0.76rem !important;
        padding: 0.66rem 0.78rem;
        border-left: 3px solid #2b62c9;
        border-radius: 0 0.55rem 0.55rem 0;
        background: #f6f8fd;
        color: #314667;
      }

      .template-product.pg-theme-v2 .pg-pdp-policy-cta {
        max-width: 88ch !important;
        margin: 0.72rem 0 0 !important;
        padding-top: 0.68rem;
        border-top: 1px solid #e6eaf0;
        color: #314667;
        font-size: 0.9rem;
      }

      .template-product.pg-theme-v2 .pg-pdp-policy-support {
        margin-top: 0.68rem !important;
        color: #314667;
        font-weight: 650;
      }

      @media screen and (max-width: 749px) {
        .template-product.pg-theme-v2 .pg-pdp-accordion__panel--policy {
          padding-bottom: 1rem;
          line-height: 1.58;
        }

        .template-product.pg-theme-v2 .pg-pdp-policy-heading {
          gap: 0.48rem;
          margin: 0.78rem 0 0.28rem !important;
        }

        .template-product.pg-theme-v2 .pg-pdp-policy-heading::before {
          width: 0.38rem;
          height: 0.38rem;
          flex-basis: 0.38rem;
        }

        .template-product.pg-theme-v2 .pg-pdp-policy-list {
          gap: 0.28rem;
          margin-block: 0.36rem 0.58rem;
        }

        .template-product.pg-theme-v2 .pg-pdp-policy-note {
          padding: 0.58rem 0.66rem;
        }
      }
    `;

    document.head.append(style);
  };

  const stripEmojiText = (element) => {
    const walker = document.createTreeWalker(element, NodeFilter.SHOW_TEXT);
    const nodes = [];

    while (walker.nextNode()) nodes.push(walker.currentNode);

    nodes.forEach((node) => {
      const cleaned = (node.nodeValue || '')
        .replace(emojiPattern, '')
        .replace(/\uFE0F/g, '')
        .replace(/\s{2,}/g, ' ');

      if (cleaned !== node.nodeValue) node.nodeValue = cleaned;
    });
  };

  const polishPanel = (panel) => {
    if (!(panel instanceof HTMLElement) || panel.dataset.pgPolicyPolished === 'true') return;

    stripEmojiText(panel);

    panel.querySelectorAll('.pg-pdp-accordion__lead').forEach((element) => {
      element.classList.add('pg-pdp-policy-heading');
    });

    panel.querySelectorAll('.pg-pdp-accordion__list').forEach((element) => {
      element.classList.add('pg-pdp-policy-list');
    });

    panel.querySelectorAll(':scope > p').forEach((paragraph) => {
      const text = normalize(paragraph.textContent).toLowerCase();

      if (text.startsWith('tous les produits indiqués comme scellés')) {
        paragraph.classList.add('pg-pdp-policy-note');
      }

      if (text.startsWith('rejoignez le discord poke-geek')) {
        paragraph.classList.add('pg-pdp-policy-cta');
      }

      if (text.startsWith('une question ?')) {
        paragraph.classList.add('pg-pdp-policy-support');
      }
    });

    panel.dataset.pgPolicyPolished = 'true';
  };

  const initializePolicyPolish = (scope = document) => {
    injectStyles();

    if (scope instanceof Element && scope.matches('.pg-pdp-accordion__panel--policy')) {
      polishPanel(scope);
    }

    scope.querySelectorAll?.('.pg-pdp-accordion__panel--policy').forEach(polishPanel);
  };

  initializePolicyPolish();

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => initializePolicyPolish(), { once: true });
  } else {
    initializePolicyPolish();
  }

  window.addEventListener('load', () => initializePolicyPolish(), { once: true });

  document.addEventListener('shopify:section:load', (event) => {
    initializePolicyPolish(event.target);
  });
})();
